import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { FeatureFlagKey, FEATURE_FLAGS, ResponsibleTeam, FeatureFlagCategory } from './index';
import { AuthService } from '../../providers/auth.service';

export interface FeatureFlag {
    key: FeatureFlagKey;
    label: string;
    description: string;
    category: FeatureFlagCategory;
    defaultValue: boolean;
    developmentOnly: boolean;
    currentValue?: boolean;
    responsibleTeam?: ResponsibleTeam;
}

export interface FeatureFlagState {
    [flagKey: string]: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class FeatureFlagService {
    private readonly storageKey = 'chargeye-feature-flags';
    private flagState$: BehaviorSubject<FeatureFlagState>;

    // Legacy flags array - now using centralized FEATURE_FLAGS definitions
    private readonly availableFlags: FeatureFlag[] = [
        // All flags are now defined in feature-flag-definitions.ts
        // This array is kept empty for backward compatibility
    ];

    private authService: AuthService | null = null;

    constructor(private injector: Injector) {
        // Initialize the flag state after availableFlags is defined
        this.flagState$ = new BehaviorSubject<FeatureFlagState>(this.loadFlagState());

        // Lazy-load AuthService to avoid circular dependency
        setTimeout(() => {
            try {
                this.authService = this.injector.get(AuthService);
            } catch (error) {
                console.warn('AuthService not available for feature flags permission checking:', error);
            }
        }, 0);

        // Auto-save flag state when it changes
        this.flagState$.subscribe((state) => {
            this.saveFlagState(state);
        });

        // Setup global console commands
        this.setupGlobalCommands();
    }

    /**
     * Check if current user has sysadmin permissions
     * @private
     */
    private checkSysAdminPermission(): boolean {
        // Always allow in development environment
        if (!environment.production) {
            return true;
        }

        // If AuthService is not available, deny access in production
        if (!this.authService) {
            return false;
        }

        try {
            const permissions = this.authService.getPermissions();
            return permissions.isSystemAdmin;
        } catch (error) {
            console.warn('Failed to check sysadmin permissions:', error);
            return false;
        }
    }

    /**
     * Validate permission and throw error if not authorized
     * @private
     */
    private validatePermission(): void {
        if (!this.checkSysAdminPermission()) {
            throw new Error('Feature flag management requires system administrator privileges');
        }
    }

    /**
     * Register this service's usage of feature flags for tracking
     * @private
     */

    /**
     * Get all available feature flags for current environment
     * Uses centralized FEATURE_FLAGS definitions
     */
    getAvailableFlags(): FeatureFlag[] {
        // Get flags from centralized definitions
        return Object.values(FEATURE_FLAGS)
            .filter((flag) => !flag.developmentOnly || !environment.production)
            .map((flag) => ({
                key: flag.key,
                label: flag.label,
                description: flag.description,
                category: flag.category,
                defaultValue: flag.defaultValue,
                developmentOnly: flag.developmentOnly,
                currentValue: this.isEnabled(flag.key),
                responsibleTeam: flag.responsibleTeam,
            }));
    }

    /**
     * Get flags grouped by category
     */
    getFlagsByCategory(): Record<FeatureFlagCategory, FeatureFlag[]> {
        const flags = this.getAvailableFlags();
        const grouped: Record<FeatureFlagCategory, FeatureFlag[]> = {} as any;

        // Initialize all categories
        Object.values(FeatureFlagCategory).forEach((category) => {
            grouped[category] = [];
        });

        // Group flags by category
        flags.forEach((flag) => {
            grouped[flag.category].push(flag);
        });

        return grouped;
    }

    /**
     * Check if a string is a valid feature flag key
     */
    isValidFlagKey(flagKey: string): boolean {
        return Object.values(FEATURE_FLAGS).some((f) => f.key === flagKey);
    }

    /**
     * Check if a feature flag is enabled
     */
    isEnabled(flagKey: FeatureFlagKey): boolean {
        const flag = Object.values(FEATURE_FLAGS).find((f) => f.key === flagKey);

        // Return false for development-only flags in production
        if (flag?.developmentOnly && environment.production) {
            return false;
        }

        const currentState = this.flagState$.value;
        return currentState[flagKey] ?? flag?.defaultValue ?? false;
    }

    /**
     * Get an observable that emits whenever a specific feature flag changes
     */
    isEnabled$(flagKey: FeatureFlagKey): Observable<boolean> {
        const flag = Object.values(FEATURE_FLAGS).find((f) => f.key === flagKey);

        return this.flagState$.pipe(
            map((state) => {
                // Return false for development-only flags in production
                if (flag?.developmentOnly && environment.production) {
                    return false;
                }
                return state[flagKey] ?? flag?.defaultValue ?? false;
            }),
            distinctUntilChanged()
        );
    }

    /**
     * Set a feature flag value
     */
    setFlag(flagKey: string, enabled: boolean): void {
        // Validate sysadmin permission for flag management
        this.validatePermission();

        const flag = Object.values(FEATURE_FLAGS).find((f) => f.key === flagKey);

        if (!flag) {
            console.warn(`Unknown feature flag: ${flagKey}`);
            return;
        }

        // Prevent setting development-only flags in production
        if (flag.developmentOnly && environment.production) {
            console.warn(`Cannot set development-only flag '${flagKey}' in production`);
            return;
        }

        const currentState = this.flagState$.value;
        const newState = { ...currentState, [flagKey]: enabled };
        this.flagState$.next(newState);
    }

    /**
     * Toggle a feature flag
     */
    toggleFlag(flagKey: FeatureFlagKey): boolean {
        const currentValue = this.isEnabled(flagKey);
        const newValue = !currentValue;
        this.setFlag(flagKey, newValue);
        return newValue;
    }

    /**
     * Enable a feature flag
     */
    enableFlag(flagKey: string): void {
        this.setFlag(flagKey, true);
    }

    /**
     * Disable a feature flag
     */
    disableFlag(flagKey: string): void {
        this.setFlag(flagKey, false);
    }

    /**
     * Get currently enabled flags
     */
    getEnabledFlags(): FeatureFlag[] {
        return this.getAvailableFlags().filter((flag) => flag.currentValue);
    }

    /**
     * Get flag state as observable
     */
    public getFlagState$(): Observable<FeatureFlagState> {
        return this.flagState$.asObservable();
    }

    public getCurrentFlagState(): FeatureFlagState {
        return this.flagState$.value;
    }

    /**
     * Enable all flags in a category
     */
    enableCategory(category: FeatureFlagCategory): void {
        // Validate sysadmin permission for category management
        this.validatePermission();

        const flagsInCategory = this.getAvailableFlags().filter((f) => f.category === category);
        flagsInCategory.forEach((flag) => this.setFlag(flag.key, true));

        console.log(`🏁 Enabled all flags in category: ${category}`);
    }

    /**
     * Disable all flags in a category
     */
    disableCategory(category: FeatureFlagCategory): void {
        // Validate sysadmin permission for category management
        this.validatePermission();

        const flagsInCategory = this.getAvailableFlags().filter((f) => f.category === category);
        flagsInCategory.forEach((flag) => this.setFlag(flag.key, false));

        console.log(`🏁 Disabled all flags in category: ${category}`);
    }

    /**
     * Enable all available flags
     */
    enableAll(): void {
        // Validate sysadmin permission for bulk operations
        this.validatePermission();

        this.getAvailableFlags().forEach((flag) => this.setFlag(flag.key, true));
        console.log('🏁 Enabled all available feature flags');
    }

    /**
     * Disable all flags (reset to defaults)
     */
    disableAll(): void {
        // Validate sysadmin permission for bulk operations
        this.validatePermission();

        this.getAvailableFlags().forEach((flag) => this.setFlag(flag.key, flag.defaultValue));
        console.log('🏁 Reset all flags to default values');
    }

    /**
     * Reset to default values
     */
    resetToDefaults(): void {
        const defaultState: FeatureFlagState = {};
        Object.values(FEATURE_FLAGS).forEach((flag) => {
            defaultState[flag.key] = flag.defaultValue;
        });

        this.flagState$.next(defaultState);
        console.log('🏁 Reset all feature flags to defaults');
    }

    /**
     * Export current flag configuration
     */
    exportConfig(): string {
        const config = {
            flags: this.flagState$.value,
            timestamp: new Date().toISOString(),
            environment: environment.production ? 'production' : 'development',
        };
        return JSON.stringify(config, null, 2);
    }

    /**
     * Import flag configuration
     */
    importConfig(configJson: string): boolean {
        // Validate sysadmin permission for importing configuration
        this.validatePermission();

        try {
            const config = JSON.parse(configJson);
            if (config && config.flags) {
                // Validate and filter flags for current environment
                const validState: FeatureFlagState = {};
                const allFlags = Object.values(FEATURE_FLAGS);
                Object.entries(config.flags).forEach(([key, value]) => {
                    const flag = allFlags.find((f) => f.key === key);
                    if (flag && (!flag.developmentOnly || !environment.production)) {
                        validState[key] = Boolean(value);
                    }
                });

                this.flagState$.next(validState);
                console.log('🏁 Imported feature flag configuration');
                return true;
            }
        } catch (error) {
            console.error('Failed to import configuration:', error);
        }
        return false;
    }

    private loadFlagState(): FeatureFlagState {
        try {
            const stored = localStorage.getItem(this.storageKey);
            if (stored) {
                const state = JSON.parse(stored);
                // Validate stored state against available flags from centralized definitions
                const validState: FeatureFlagState = {};
                const allFlags = Object.values(FEATURE_FLAGS);

                if (allFlags && allFlags.length > 0) {
                    Object.entries(state).forEach(([key, value]) => {
                        const flag = allFlags.find((f) => f.key === key);
                        if (flag && (!flag.developmentOnly || !environment.production)) {
                            validState[key] = Boolean(value);
                        }
                    });
                } else {
                    // Fallback: return state as-is
                    Object.entries(state).forEach(([key, value]) => {
                        validState[key] = Boolean(value);
                    });
                }
                return validState;
            }
        } catch (error) {
            console.warn('Failed to load feature flag state:', error);
        }

        // Return default state from centralized definitions
        const defaultState: FeatureFlagState = {};
        Object.values(FEATURE_FLAGS).forEach((flag) => {
            if (!flag.developmentOnly || !environment.production) {
                defaultState[flag.key] = flag.defaultValue;
            }
        });
        return defaultState;
    }

    private saveFlagState(state: FeatureFlagState): void {
        try {
            localStorage.setItem(this.storageKey, JSON.stringify(state));
        } catch (error) {
            console.warn('Failed to save feature flag state:', error);
        }
    }

    /**
     * Check if ANY development/debugging flags are enabled
     * This provides backward compatibility with DevelopmentService.devFeaturesEnabled$()
     *
     * Returns true if any flag marked as developmentOnly=true is currently enabled
     */
    public anyDevelopmentFlagsEnabled$(): Observable<boolean> {
        return new Observable((observer) => {
            const subscription = this.flagState$.subscribe((state) => {
                const developmentFlags = Object.values(FEATURE_FLAGS).filter((flag) => flag.developmentOnly);
                const anyEnabled = developmentFlags.some((flag) => state[flag.key] === true);
                observer.next(anyEnabled);
            });
            return () => subscription.unsubscribe();
        });
    }

    private setupGlobalCommands(): void {
        // Add global commands to window object
        (window as any).featureFlags = {
            // Show all flags
            list: () => {
                console.log('🏁 Available Feature Flags:');
                const flagsByCategory = this.getFlagsByCategory();
                Object.entries(flagsByCategory).forEach(([category, flags]) => {
                    if (flags.length > 0) {
                        console.log(`\n📂 ${category}:`);
                        flags.forEach((flag) => {
                            const status = flag.currentValue ? '✅' : '❌';
                            const devOnly = flag.developmentOnly ? ' [DEV ONLY]' : '';
                            console.log(`  ${status} ${flag.key}: ${flag.label}${devOnly}`);
                            console.log(`     ${flag.description}`);
                        });
                    }
                });
            },

            // Enable a flag
            enable: (flagKey: string) => {
                this.enableFlag(flagKey);
            },

            // Disable a flag
            disable: (flagKey: string) => {
                this.disableFlag(flagKey);
            },

            // Toggle a flag
            toggle: (flagKey: FeatureFlagKey) => {
                const newValue = this.toggleFlag(flagKey);
                console.log(`🏁 ${flagKey} is now ${newValue ? 'enabled' : 'disabled'}`);
            },

            // Check if flag is enabled
            check: (flagKey: FeatureFlagKey) => {
                const enabled = this.isEnabled(flagKey);
                console.log(`🏁 ${flagKey} is ${enabled ? 'enabled' : 'disabled'}`);
                return enabled;
            },

            // Show enabled flags
            enabled: () => {
                const enabled = this.getEnabledFlags();
                console.log('🏁 Currently Enabled Flags:');
                if (enabled.length === 0) {
                    console.log('  None');
                } else {
                    enabled.forEach((flag) => {
                        console.log(`  ✅ ${flag.key}: ${flag.label}`);
                    });
                }
            },

            // Category operations
            enableCategory: (category: string) => {
                this.enableCategory(category as FeatureFlagCategory);
            },

            disableCategory: (category: string) => {
                this.disableCategory(category as FeatureFlagCategory);
            },

            // Bulk operations
            enableAll: () => {
                this.enableAll();
            },

            disableAll: () => {
                this.disableAll();
            },

            reset: () => {
                this.resetToDefaults();
            },

            // Export/Import
            export: () => {
                const config = this.exportConfig();
                console.log('📤 Feature Flag Configuration:');
                console.log(config);
                return config;
            },

            import: (configJson: string) => {
                const success = this.importConfig(configJson);
                console.log(success ? '📥 Configuration imported successfully' : '❌ Failed to import configuration');
            },

            // Help
            help: () => {
                console.log(`
🏁 Feature Flag Commands

📋 VIEWING:
  featureFlags.list()              - Show all available flags
  featureFlags.enabled()           - Show currently enabled flags
  featureFlags.check('flagName')   - Check if specific flag is enabled

🔧 INDIVIDUAL FLAGS:
  featureFlags.enable('flagName')  - Enable a flag
  featureFlags.disable('flagName') - Disable a flag
  featureFlags.toggle('flagName')  - Toggle a flag

📂 CATEGORY OPERATIONS:
  featureFlags.enableCategory('App Behavior')  - Enable all flags in category
  featureFlags.disableCategory('Debugging')    - Disable all flags in category

🔄 BULK OPERATIONS:
  featureFlags.enableAll()         - Enable all available flags
  featureFlags.disableAll()        - Disable all flags
  featureFlags.reset()             - Reset to default values

💾 CONFIG MANAGEMENT:
  featureFlags.export()            - Export current configuration
  featureFlags.import(config)      - Import configuration

Examples:
  featureFlags.enable('forceMyKempowerMode')
  featureFlags.toggle('debugMode')
  featureFlags.enableCategory('UI Features')
                `);
            },
        };

        console.log('🏁 Feature Flag commands available! Type featureFlags.help() for usage');
    }
}
